import { Button, Space, Tabs } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from 'react-router';
import { myBIRCodeDataSelector } from 'screens/context/stores/selectors';
import { PageContainer } from 'shared/GlobalStyles';
import { CenteredSpin, TableWrapper } from 'shared/components/Styles';
import { useConfigTranslate } from 'shared/hooks/useConfigTranslate';
import { Payload } from 'shared/utils/types';
import { getBirCode } from 'shared/utils/utils';
import { BuyersTable } from '../components/BuyersTable';
import { SellerTable } from '../components/SellerTable';
import { useBuyersColumns } from '../hooks/useBuyersColumns';
import { accountPostgreActions, accountsSelectors, defaultBuyerPayload, getClientCodePrincipal } from '../stores';

const { TabPane } = Tabs;

/**
 * Accounts R1
 */
export const AccountsTracking: FC = () => {
  let navigation = useNavigation();

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const BuyersColumns = useBuyersColumns();

  const myBIRCodeData = useSelector(myBIRCodeDataSelector);
  const sellerData = useSelector(accountsSelectors.getSeller);
  const { payload, data } = useSelector(accountsSelectors.getBuyers)
  const columns = useSelector(accountsSelectors.buyersColumns)
  const lastVisitedTab = useSelector(accountsSelectors.buyersLastVisitedTab)

  const [buyersColumns, setBuyersColumns] = useState(lastVisitedTab && columns ? columns : BuyersColumns)
  const [buyersPayload, setBuyersPayload] = useState<Payload>(lastVisitedTab ? (payload || defaultBuyerPayload) : defaultBuyerPayload)
  const [curActiveKey, setCurActiveKey] = useState<string>(lastVisitedTab ? lastVisitedTab : "1")
  const [tableKey, setTableKey] = useState<number>(0);

  const defaultActiveKey = lastVisitedTab ? lastVisitedTab : "1"

  const reinitTab = () => {
    setBuyersColumns(BuyersColumns)
    setBuyersPayload(defaultBuyerPayload)
  }

  const handleReinitFilters = () => {
    reinitTab()
    setTableKey(tableKey + 1)
  }

  const onChange = (activeKey: string) => {
    reinitTab()
    setCurActiveKey(activeKey);
  }

  useEffect(() => { dispatch(accountPostgreActions.setBuyersColumns(buyersColumns)) }, [buyersColumns])
  useEffect(() => { dispatch(accountPostgreActions.getSeller(getBirCode()!)) }, [])
  useEffect(() => { dispatch(accountPostgreActions.getBuyers({ ...buyersPayload, sellerCode: getBirCode()! })) }, [buyersPayload])
  useEffect(() => { dispatch(accountPostgreActions.setBuyersLastVisitedTab(curActiveKey)) }, [curActiveKey])

  const csvData = data?.content.map((x => {
    return ({
      [t("Account.Columns.businessname")]: x.account.commercialName,
      [t("Account.Columns.SellersTable.tresorcode")]: x.account.tresorCode,
      [t("Account.Columns.bircode")]: x.account.sellerCode,
      [t("Account.Columns.validitystartdate")]: x.accountCommercialLinks.validityStartDate ? dayjs(x.accountCommercialLinks.validityStartDate).format('L') : '',
      [t("Account.Columns.validityenddate")]: x.accountCommercialLinks.validityEndDate ? dayjs(x.accountCommercialLinks.validityEndDate).format('L') : '',
      [t("Account.Columns.clienttype")]: x.accountCommercialLinks.type,
      [t("Account.Columns.clientcode")]: getClientCodePrincipal(x.accountClientCodes)?.clientCode?.trim() ?? ''
    })
  }))

  const tabBarExtraContent = csvData && sellerData ? <>
    <Button
      style={{ marginRight: "10px" }}
      type="primary">
      <CSVLink
        filename={`buyers-${myBIRCodeData!.bircode}.csv`}
        data={csvData}
        separator=";"
      >
        {t("Generic.Actions.ExportToCSV")}
      </CSVLink>
    </Button>
    <Button onClick={() => handleReinitFilters()}>{t("Generic.Actions.ReinitialiseFilters")}</Button>
  </> : <CenteredSpin />

  const locale = useConfigTranslate();
  useEffect(() => {
    if (!navigation?.location?.state)
      handleReinitFilters()
  }, [locale])

  return (
    <PageContainer>
      <Tabs
        destroyInactiveTabPane={true}
        defaultActiveKey={defaultActiveKey}
        onChange={onChange}
      >
        <TabPane tab={t("Account.Navigation.Seller")} key="1">
          <TableWrapper>
            <Space style={{ width: "100%" }} direction="vertical" size="large">
              <SellerTable />
              <BuyersTable tabBarExtraContent={tabBarExtraContent} setPayload={setBuyersPayload} payload={{ ...buyersPayload }} columns={[...buyersColumns]} setColumns={setBuyersColumns} key={tableKey} />
            </Space>
          </TableWrapper>
        </TabPane>
      </Tabs>
    </PageContainer>
  );
};
